import { BuildRCAttribute } from "@/common/function/RCAttribute";
import DropdownCustom from "@/components/ui/Dropdown";
import {
  CategoryIcon,
  CategoryIconActive,
  LocationIcon,
  LocationIconActive,
  ResourceTypeIcon,
  SearchIcon,
} from "@/components/ui/Icons/IconsCustom";
import TreeSelect from "@/components/ui/TreeSelect";
import { IRefComponentTypeTreeSelect } from "@/components/ui/TreeSelect/Models";
import { RootState } from "@/redux";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import { IResourceStore } from "@/redux/slices/resources/ResourceReducerModel";
import { FILTER_DEFAULT } from "@/redux/slices/resources/ResourceSlice";
import { IResourceType } from "@/services";
import { ROOT } from "@/services/resource-service/entity/Enums";
import { IFilterResource } from "@/services/resource-service/entity/ResourceEntity";
import {
  Checkbox,
  CheckboxOnChangeData,
  Input,
  Option,
  OptionOnSelectData,
  SelectionEvents
} from "@fluentui/react-components";
import { cloneDeep } from "lodash";
import { RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FloorPlanEvents } from "../floor-plan/FloorPlanEvents";
import { IFilterResourceProps } from "./Models";
import { ResourceFilterBaseWrapper, ResourceFilterGridWrapper } from "./Styles";

let timer: number | null;

const Filter = (props: IFilterResourceProps) => {
  const { t } = useTranslation();
  const refLocation: RefObject<IRefComponentTypeTreeSelect | null> =
    useRef(null);
  const refCategory: RefObject<IRefComponentTypeTreeSelect | null> =
    useRef(null);
  const resourceStore: IResourceStore = useSelector(
    (state: RootState) => state.resources
  ) as IResourceStore;

  const orgStore: IOrganizationStore = useSelector(
    (state: RootState) => state.orgInfo
  ) as IOrganizationStore;

  const theme: string = useSelector(
    (state: RootState) => state.settingInfo?.theme
  ) as string;

  const [filter, setFilter] = useState<IFilterResource>(
    resourceStore.filterResource || FILTER_DEFAULT
  );

  const resourceTypes: IResourceType[] = orgStore?.orgInfo?.resourceTypes || [];

  const [mapOption, setOption] = useState<Record<string, string>>({});

  useEffect(() => {
    if (resourceTypes?.length) {
      const mapOptionClone: Record<string, string> = {}
      resourceTypes.forEach(item => {
        mapOptionClone[item.key] = item.text;
      });
      setOption(mapOptionClone)
    }
  }, [JSON.stringify(resourceTypes)]);

  useEffect(() => {
    if (resourceStore.filterResource) {
      setFilter(resourceStore.filterResource);
    }
  }, [JSON.stringify(resourceStore.filterResource)]);

  const onChangeLocations = (arr: string[]): void => {
    const filterClone: IFilterResource = cloneDeep(filter);
    filterClone.locationIds = [...arr];
    setFilter(filterClone);
  };

  const onChangeCategory = (arr: string[]): void => {
    const filterClone: IFilterResource = cloneDeep(filter);
    filterClone.categoryIds = [...arr];
    setFilter(filterClone);
  };

  const handleChangeResourceName = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const filterClone: IFilterResource = cloneDeep(filter);
    filterClone.name = event.target.value || "";
    setFilter(filterClone);
    if (timer) {
      clearTimeout(timer);
    }
    timer = window.setTimeout(() => {
      if (
        props.updateFilterResource &&
        JSON.stringify(filterClone) !==
          JSON.stringify(resourceStore.filterResource)
      ) {
        props.updateFilterResource(filterClone);
      }
    }, 2000);
  };

  const changeIsAvailable = (_ev: any, data: CheckboxOnChangeData) => {
    const filterClone: IFilterResource = cloneDeep(filter);
    filterClone.isAvailable = !!data.checked;
    setFilter(filterClone);
    if (timer) {
      clearTimeout(timer);
    }
    timer = window.setTimeout(() => {
      if (
        props.updateFilterResource &&
        JSON.stringify(filterClone) !==
          JSON.stringify(resourceStore.filterResource)
      ) {
        props.updateFilterResource(filterClone);
      }
    }, 500);
  };

  const onOptionSelect = (
    _event: SelectionEvents,
    data: OptionOnSelectData
  ) => {
    const filterClone: IFilterResource = cloneDeep(filter);
    filterClone.resourceTypes = data.optionValue;
    setFilter(filterClone);
    if (timer) {
      clearTimeout(timer);
    }
    timer = window.setTimeout(() => {
      if (
        props.updateFilterResource &&
        JSON.stringify(filterClone) !==
        JSON.stringify(resourceStore.filterResource)
      ) {
        props.updateFilterResource(filterClone);
      }
    }, 500);
  };

  const onCheckCondition = (condition: string) => {
    const filterClone: IFilterResource = cloneDeep(filter);
    filterClone.categoryCondition = condition;
    setFilter(filterClone);
  };

  const onApply = (): void => {
    if (
      props.updateFilterResource &&
      JSON.stringify(filter) !== JSON.stringify(resourceStore.filterResource)
    ) {
      const filterClone: IFilterResource = cloneDeep(filter);
      filterClone.indexLocation = 0;
      props.updateFilterResource(filterClone);
    }
    if (refLocation.current?.onChangeToggleContextMenu) {
      refLocation.current?.onChangeToggleContextMenu(false);
    }
    if (refCategory.current?.onChangeToggleContextMenu) {
      refCategory.current?.onChangeToggleContextMenu(false);
    }
  };

  const onClear = (_arr?: string[], key?: string): void => {
    const filterClone: IFilterResource = cloneDeep(filter);
    if (key === "location") {
      filterClone.locationIds = [];
      filterClone.indexLocation = 0;
    } else if (key === "category") {
      filterClone.categoryIds = [];
      filterClone.categoryCondition = "or";
    }
    if (props.updateFilterResource) {
      props.updateFilterResource(filterClone);
    }
    if (refLocation.current?.onChangeToggleContextMenu) {
      refLocation.current?.onChangeToggleContextMenu(false);
    }
    if (refCategory.current?.onChangeToggleContextMenu) {
      refCategory.current?.onChangeToggleContextMenu(false);
    }
  };

  useEffect(() => {
    const hideDropdown = () => {
      refLocation.current?.onChangeToggleContextMenu?.(false);
    };
    const hideDropdownCategory = () => {
      refCategory.current?.onChangeToggleContextMenu?.(false);
    };

    FloorPlanEvents.ElementMouseOver.Subscribe(hideDropdown);
    FloorPlanEvents.ElementMouseOver.Subscribe(hideDropdownCategory);
    return () => {
      FloorPlanEvents.ElementMouseOver.UnSubscribe();
      FloorPlanEvents.ElementMouseOver.UnSubscribe();
    };
  }, []);

  return (
    <ResourceFilterBaseWrapper
      className="ResourceFilterBaseWrapper"
      $theme={theme}
    >
      <ResourceFilterGridWrapper
        className="ResourceFilterGridWrapper ms-Grid"
        dir="ltr"
      >
        {filter ? (
          <div className="ms-Grid-row dropdown-filter">
            <div className="ms-Grid-col ms-sm6 pr-4">
              <TreeSelect
                items={orgStore?.locationTrees || []}
                values={filter.locationIds || []}
                size="small"
                onChange={onChangeLocations}
                iconLeft={<LocationIcon theme={theme} />}
                iconLeftActive={<LocationIconActive theme={theme} />}
                displayLabelValue={t("Locations")}
                lblSelect={t("Locations")}
                style={{ minWidth: "100%" }}
                onApply={onApply}
                onClear={(arr?: string[]) => onClear(arr, "location")}
                expanded={[orgStore.orgInfo?.tenantId || ROOT]}
                theme={theme}
                rcName="filter_location"
                ref={refLocation}
              />
            </div>
            <div className="ms-Grid-col ms-sm6 pl-4 custom-dropdown">
              <DropdownCustom
                placeholder={t("Select an resource type")}
                selectedOptions={[filter.resourceTypes || ""]}
                value={mapOption[filter.resourceTypes || ""]}
                onOptionSelect={onOptionSelect}
                iconLeft={
                  <ResourceTypeIcon
                    isActive={!!filter.resourceTypes}
                    theme={theme}
                  />
                }
                size="small"
                clearable={!!filter.resourceTypes}
              >
                {resourceTypes.map((item: IResourceType) => (
                  <Option key={item.key} value={item.key} className="custom-option-small">
                    {item.text}
                  </Option>
                ))}
              </DropdownCustom>
            </div>
            <div className="ms-Grid-col ms-sm6 pr-4 mt-8">
              <TreeSelect
                items={orgStore?.categoryTrees || []}
                values={filter.categoryIds || []}
                condition={filter.categoryCondition || ""}
                size="small"
                showRadio
                onChange={onChangeCategory}
                onCheckCondition={onCheckCondition}
                iconLeft={<CategoryIcon theme={theme} />}
                iconLeftActive={<CategoryIconActive theme={theme} />}
                displayLabelValue={t("Categories")}
                lblSelect={t("Categories")}
                onApply={onApply}
                onClear={(arr?: string[]) => onClear(arr, "category")}
                expanded={[orgStore.orgInfo?.tenantId || ROOT]}
                theme={theme}
                rcName="filter_category"
                ref={refCategory}
              />
            </div>
            <div className="ms-Grid-col ms-sm6 mt-8 pl-4">
              <Input
                value={filter.name || ""}
                style={{ width: "100%" }}
                contentBefore={<SearchIcon theme={theme} />}
                id="search-box"
                placeholder={t("Search resource name...")}
                onChange={handleChangeResourceName}
                size="small"
                // contentAfter={
                //   <Switch
                //     checked={filter.isAvailable}
                //     label={t("Available")}
                //     labelPosition="before"
                //     onChange={changeIsAvailable}
                //   />
                // }
                {...BuildRCAttribute(`filter_input_search`)}
              />
            </div>
            <div className="ms-Grid-col ms-sm12 mt-8 custom-checkbox">
              <Checkbox
                checked={filter.isAvailable}
                onChange={changeIsAvailable}
                label={t("Available Resources")}
              />
            </div>
          </div>
        ) : null}
      </ResourceFilterGridWrapper>
    </ResourceFilterBaseWrapper>
  );
};

export default Filter;
